import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { namespace } from '@common-config/constants';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { entitlementUnknownSelector, selectCurrentUserPhone } from '@common-reducers/UserSelectors';
import { DBSubscriptionThunks } from '@common/reducers/DBServiceThunks';
import { useOpenFirstWeekComposer } from '@common/components/first-week-card/use-open-first-week-composer';
import { usePlanData } from '@common/components/plan-selection-v2/use-plan-data';
import { AnalyticsService } from '@common/services/analytics-service';
import { authService } from '@/common/bt-auth/auth-service';
import { getProTrialEligibility, startProTrial } from '@extension/wa-communication-layer/api/api-calls';
import { refreshSessions } from '../engines-management/refresh-sessions';
import { useRemoteConnection } from '../engines-management/use-remote-connection';
import type { DisplayEngine } from '../engines-management/types';
import { conditionsFor, localSupport, LocalSupport, FlowScreen, localSubstate, LocalSubstate, messageDoneFrom, OnboardingPath, ownWidFromEngines, ownWidFromPhone, pendingGateway } from './onboarding-logic';
import { markUpgradePending } from './onboarding-resume';
import { trackStartTrial } from '@common/services/facebook-conversions';
import { eligibilityTrialState, trialOutcome } from './trial-outcome';
import { readStoredPath, useLiveConnectState, writeStoredPath } from './use-setup-status';

export type ConnectSurface = 'onboarding' | 'connections';
export type TrialState = 'checking' | 'idle' | 'busy' | 'unlocking' | 'used' | 'unavailable' | 'error';

const STARTED_KEY = 'bt-onboarding-started-v1';
/** #691: how long the wizard waits for `pro-trial-eligibility` before offering the trial anyway. */
const TRIAL_CHECK_TIMEOUT_MS = 15_000;
/** Dev-only ?preview=remote-link: a gateway sitting on a QR, to review the linking screen. */
const PREVIEW_ENGINE = { id: 'preview', type: 'gateway', name: 'preview', status: 'qrcode', domain: '', connectionTime: '', canDelete: false, canRestart: false, qrCode: '2@preview-onboarding-qr-code', session: { _id: 'preview', status: 'qrcode' } } as DisplayEngine;
const PREVIEW_SUBSTATE: Record<string, LocalSubstate> = { 'local-wa': 'openWa', 'local-other': 'otherAccount', 'local-signedout': 'signedOut', 'local-linking': 'linking' };

const storage = {
  get: (k: string, s: Storage | undefined) => { try { return s?.getItem(k) ?? null; } catch { return null; } },
  set: (k: string, v: string | null, s: Storage | undefined) => { try { if (v === null) s?.removeItem(k); else s?.setItem(k, v); } catch { /* blocked */ } },
};
const session = typeof window !== 'undefined' ? window.sessionStorage : undefined;

/**
 * Everything the connect wizard knows and does — one implementation for /onboarding (full
 * screen) and /connections (in the card). Every screen is derived from REAL state (the live
 * connection, the extension's ping, the plan, the server's first-week checklist), so leaving and
 * coming back lands on the right condition. Only the chosen path is remembered locally.
 */
export function useConnectFlow({ surface, onEvent, onManage, onAlwaysOnPro }: {
  surface: ConnectSurface;
  /** The host mirrors wizard events (e.g. /connections' backend funnel). */
  onEvent?: (name: string, props?: Record<string, unknown>) => void;
  /** "Manage your connections" — defaults to /connections. */
  onManage?: () => void;
  /** A Pro user on the done screen asks for always-on — defaults to /connections. */
  onAlwaysOnPro?: () => void;
}) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Dev-only: ?preview=choose|mobile|local|local-wa|local-other|local-signedout|local-linking|
  // local-anim|remote|remote-pro|remote-link|message|done renders a screen regardless of this
  // browser's real connection (QA + design review). Ignored in production.
  const preview = process.env.NODE_ENV === 'development' ? new URLSearchParams(window.location.search).get('preview') : null;

  const live = useLiveConnectState();
  const { user, hasExtension, ping, checklist, refetchChecklist, displayEngines } = live;
  const accessToken = useAppSelector((state) => state.UserReducer.accessToken);
  const entitlementUnknown = useAppSelector(entitlementUnknownSelector);
  const kind = preview ? (preview === 'message' || preview === 'done' ? 'local' : 'none') : live.liveKind;
  const userPhone = useAppSelector(selectCurrentUserPhone);
  const openComposer = useOpenFirstWeekComposer('web');
  const { plans } = usePlanData();
  const { createRemote, ensureRunning, isCreating } = useRemoteConnection();

  useEffect(() => { dispatch(refreshSessions()); }, [dispatch]);
  // Fresh plan on entry — someone coming back from checkout must see always-on unlocked.
  useEffect(() => { dispatch(DBSubscriptionThunks.find({})); }, [dispatch]);

  const isPro = preview === 'remote-pro' || preview === 'remote-link' || (preview !== 'remote' && live.allowedEngines > 0);
  const proPlan = plans.find(p => p.key === 'Campaigner');
  const proPrice = proPlan && proPlan.monthly > 0 ? `$${proPlan.monthly}` : null;
  // What this device can run — from the device itself, never the window width (?preview=mobile|otherbrowser for review).
  const device: LocalSupport = useMemo(() => (
    preview === 'mobile' ? 'phone'
      : preview === 'otherbrowser' ? 'otherBrowser'
        : localSupport(
          typeof navigator !== 'undefined' ? navigator.userAgent : '',
          typeof navigator !== 'undefined' ? navigator.maxTouchPoints ?? 0 : 0,
          typeof navigator !== 'undefined' ? (navigator as { userAgentData?: { platform?: string } }).userAgentData?.platform : undefined,
        )
  ), [preview]);

  const [path, setPathState] = useState<OnboardingPath | null>(readStoredPath);
  // "Choose another way": the choice screen wins (even over a pending remote) until they pick again.
  const [chooseAgain, setChooseAgain] = useState(false);
  const setPath = useCallback((p: OnboardingPath | null) => { writeStoredPath(p); setPathState(p); setChooseAgain(p === null); }, []);

  // Hold the screen (and every event) until the live state settled — see useLiveConnectState.
  const ready = !!preview || live.ready;

  // A remote connection that exists but is not linked yet (just created, waiting on a scan, or
  // unlinked by WhatsApp) — the always-on screen resumes it instead of creating a second one.
  const [createdId, setCreatedId] = useState<string | null>(null);
  const pendingEngine: DisplayEngine | undefined = useMemo(() => pendingGateway(displayEngines, createdId), [displayEngines, createdId]);
  // The engine we created vanished from the store → the server reaped it (no scan in time).
  const lastEngineRef = useRef<DisplayEngine | undefined>(undefined);
  if (pendingEngine) lastEngineRef.current = pendingEngine;
  const expired = !!createdId && !pendingEngine && lastEngineRef.current?.id === createdId;
  const remoteEngine = pendingEngine ?? (expired ? lastEngineRef.current : undefined);
  // Pro, nothing pending, but every always-on slot is taken (e.g. one that stopped) → manage it.
  const gatewayCount = displayEngines.filter(e => e.type === 'gateway' && !e.isLegacy).length;
  const atLimit = !preview && isPro && !pendingEngine && gatewayCount >= live.allowedEngines;

  // "This computer": which account the extension uses, and where exactly it is stuck.
  const liveAccount = live.account;
  const liveSubstate = localSubstate({ hasExtension, waTabOpen: !!ping?.isWhatsAppTabTracked, account: liveAccount });
  const [animSubstate, setAnimSubstate] = useState<LocalSubstate>('install');
  useEffect(() => {
    if (preview !== 'local-anim') return undefined;
    const seq: LocalSubstate[] = ['install', 'openWa', 'linking'];
    let i = 0;
    const id = window.setInterval(() => { i = (i + 1) % seq.length; setAnimSubstate(seq[i]); }, 2600);
    return () => window.clearInterval(id);
  }, [preview]);
  const substate: LocalSubstate = preview === 'local-anim' ? animSubstate : (preview && PREVIEW_SUBSTATE[preview]) || (preview === 'local' ? 'install' : liveSubstate);

  // The whole wizard in one decision: the first condition the user hasn't met yet.
  const [deliveredHere, setDeliveredHere] = useState(false);
  const messageDone = preview ? preview === 'done' : messageDoneFrom(checklist, deliveredHere);
  const previewPath: OnboardingPath | null = preview?.startsWith('local') ? 'local' : preview?.startsWith('remote') ? 'remote' : null;
  const flow = conditionsFor({
    kind,
    path: preview ? previewPath : path,
    chooseAgain: !preview && chooseAgain,
    hasExtension: preview ? substate !== 'install' : hasExtension,
    account: preview ? (substate === 'otherAccount' ? 'other' : substate === 'signedOut' ? 'signedOut' : 'match') : liveAccount,
    canRemote: isPro,
    // As before: a leftover gateway only resumes for an entitled user.
    pendingRemote: !preview && !!pendingEngine && isPro,
    messageDone,
  });
  const step: FlowScreen = flow.screen;

  // The user's own WhatsApp id: the checklist (server: whatsappId / phone / the connected session's
  // number), the profile, or — for an always-on connection — the number the engine stored on its
  // session when it linked. Right after connecting each can lag a few seconds, so keep re-asking
  // (checklist AND sessions) until one lands.
  const ownWid = checklist?.ownWid
    ?? ownWidFromPhone((user as { whatsappId?: string } | undefined)?.whatsappId)
    ?? ownWidFromPhone(userPhone)
    ?? ownWidFromEngines(displayEngines);
  const [findingNumber, setFindingNumber] = useState(false);
  useEffect(() => {
    if (preview || !ready || step !== 'message' || ownWid) { setFindingNumber(false); return undefined; }
    setFindingNumber(true);
    const ask = () => { refetchChecklist(); dispatch(refreshSessions()); };
    const tick = window.setInterval(ask, 3000);
    const stop = window.setTimeout(() => { window.clearInterval(tick); setFindingNumber(false); }, 120_000);
    ask();
    return () => { window.clearInterval(tick); window.clearTimeout(stop); };
  }, [preview, ready, step, ownWid, refetchChecklist, dispatch]);

  // Time-to-value clock: from the first visit of this browser session.
  const startedAt = useMemo(() => {
    const v = Number(storage.get(STARTED_KEY, session));
    if (v > 0) return v;
    const now = Date.now();
    storage.set(STARTED_KEY, String(now), session);
    return now;
  }, []);

  /* ── analytics: every event carries surface, path, plan and entry so the funnel can be cut ── */
  const analytics = useMemo(() => new AnalyticsService(), []);
  const entry = useMemo(() => new URLSearchParams(window.location.search).get('from') || 'direct', []);
  const track = useCallback((name: string, props?: Record<string, unknown>) => {
    try {
      analytics.event(name, { category: 'onboarding', surface, path: flow.path ?? 'none', is_pro: isPro, entry, ...(props ?? {}) });
    } catch { /* never break onboarding over analytics */ }
    try { onEvent?.(name, props); } catch { /* the host's mirror is best-effort */ }
  }, [analytics, surface, flow.path, isPro, entry, onEvent]);

  // A pending gateway whose pod was torn down can never show a QR — start it.
  useEffect(() => {
    if (!preview && step === 'remote' && isPro && pendingEngine) ensureRunning(pendingEngine.session);
  }, [preview, step, isPro, pendingEngine, ensureRunning]);

  // L2 in one click: the web app hands its own sign-in to the extension (the background stores it
  // and passes it on to WhatsApp Web); the next ping then reports the matching account.
  const [link, setLink] = useState<'idle' | 'linking' | 'failed'>('idle');
  /** Returns false when there is nothing to hand over (no sign-in yet). */
  const linkAccount = useCallback((auto = false) => {
    if (!accessToken) return false;
    track('onboarding_extension_link', { from: substate, ...(auto ? { auto: true } : {}) });
    setLink('linking');
    authService.persistToken(accessToken);
    return true;
  }, [accessToken, track, substate]);
  const accountMatches = liveAccount !== 'signedOut' && liveAccount !== 'other';
  useEffect(() => {
    if (link !== 'linking') return undefined;
    if (accountMatches) { setLink('idle'); track('onboarding_extension_linked'); return undefined; }
    // No answer from the extension → say so and offer the by-hand way (sign in inside WhatsApp Web).
    const id = window.setTimeout(() => { setLink('failed'); track('onboarding_stuck', { step: 'extension_link' }); }, 15_000);
    return () => window.clearTimeout(id);
  }, [link, accountMatches]); // eslint-disable-line react-hooks/exhaustive-deps

  // R1: a free trial first; the paid plan only when the trial isn't available.
  const [trial, setTrial] = useState<TrialState>(preview ? 'idle' : 'checking');
  // #691: ask first, so someone who already had a trial (this workspace or this email) is shown the
  // paid plan straight away instead of a "Start trial" button the server would refuse.
  useEffect(() => {
    if (preview) return undefined;
    let alive = true;
    const settle = (next: 'idle' | 'used' | 'unavailable') => { if (alive) setTrial((cur) => (cur === 'checking' ? next : cur)); };
    // Never leave the button spinning: a slow answer falls back to offering the trial.
    const giveUp = window.setTimeout(() => settle('idle'), TRIAL_CHECK_TIMEOUT_MS);
    getProTrialEligibility()
      .then((res) => eligibilityTrialState(res))
      .catch((): 'idle' => 'idle')
      .then(settle);
    return () => { alive = false; window.clearTimeout(giveUp); };
  }, [preview]);
  const startTrial = useCallback(async () => {
    track('onboarding_trial_clicked');
    setTrial('busy');
    let outcome: ReturnType<typeof trialOutcome>;
    try {
      const res = await startProTrial();
      outcome = trialOutcome(res);
      if (outcome === 'started') trackStartTrial(res.subscriptionId);
    } catch { outcome = 'error'; }
    if (outcome === 'started') track('onboarding_trial_started');
    else track('onboarding_trial_unavailable', { reason: outcome });
    setTrial(outcome === 'started' ? 'unlocking' : outcome);
  }, [track]);
  // Started → re-read the plan until it allows always-on (R1 met moves the wizard on by itself).
  // The subscription list can lag a few seconds behind the trial; never offer the trial twice.
  useEffect(() => {
    if (trial !== 'unlocking' || isPro) return undefined;
    dispatch(DBSubscriptionThunks.find({}));
    const tick = window.setInterval(() => dispatch(DBSubscriptionThunks.find({})), 3000);
    const stop = window.setTimeout(() => { window.clearInterval(tick); setTrial('error'); }, 30_000);
    return () => { window.clearInterval(tick); window.clearTimeout(stop); };
  }, [trial, isPro, dispatch]);

  // Take them to their WhatsApp Web tab (the extension focuses the existing one). An older
  // extension doesn't know the command — open WhatsApp Web the plain way instead.
  const goToWhatsApp = useCallback(() => {
    track('onboarding_go_to_whatsapp', { state: substate });
    if (ping?.reportsSignedInUser) window.postMessage({ type: 'BT_FOCUS_WHATSAPP' }, window.location.origin);
    else window.open('https://web.whatsapp.com', '_blank', 'noopener,noreferrer');
  }, [track, substate, ping?.reportsSignedInUser]);

  const shareLink = async () => {
    const url = `${window.location.origin}/onboarding`;
    track('onboarding_share_desktop_link');
    try {
      // A phone hands the link on (share sheet); a computer in Safari/Firefox just needs it pasted into Chrome.
      if (device === 'phone' && navigator.share) { await navigator.share({ url, title: 'Blueticks' }); return false; }
      await navigator.clipboard.writeText(url);
      return true;
    } catch { return false; }
  };

  const finish = (to: string) => { track('onboarding_complete', { ms: Date.now() - startedAt }); navigate(to); };

  return {
    ready,
    preview: !!preview,
    flow,
    kind,
    hasExtension,
    entry,
    device,
    isPro,
    proPrice,
    substate,
    canTellAccount: !!ping?.reportsSignedInUser,
    email: user?.email ?? '',
    startedAt,
    track,
    remote: {
      loading: entitlementUnknown,
      engine: isPro && !preview ? remoteEngine : preview === 'remote-link' ? PREVIEW_ENGINE : undefined,
      expired,
      isCreating,
      atLimit,
      trial,
    },
    message: {
      ownWid,
      findingNumber,
      alreadyDone: messageDone && !deliveredHere,
      /** The first message went out during THIS visit (the done screen is a fresh win). */
      deliveredHere,
    },
    actions: {
      choose: (p: OnboardingPath) => { track('onboarding_path_chosen', { path: p }); setPath(p); },
      back: () => setPath(null),
      remoteBack: () => { if (!isPro) track('onboarding_paywall_exit', { action: 'back' }); setPath(null); },
      goToWhatsApp,
      linkAccount: () => { linkAccount(); },
      link,
      shareLink,
      video: () => track('onboarding_video_play'),
      startTrial,
      upgrade: () => { track('onboarding_paywall_exit', { action: 'upgrade' }); markUpgradePending('paywall'); setPath('remote'); navigate('/account-subscription'); },
      useLocal: () => { track('onboarding_paywall_exit', { action: 'fallback_local' }); setPath('local'); },
      createRemote: () => { track('onboarding_remote_started'); setCreatedId(null); createRemote((id) => setCreatedId(id)); },
      manage: () => (onManage ? onManage() : navigate('/connections')),
      scheduleReal: () => { openComposer('workflow'); finish('/calendar'); },
      alwaysOnFromDone: () => {
        track('onboarding_always_on_clicked', { source: 'done_upsell' });
        if (isPro) { if (onAlwaysOnPro) onAlwaysOnPro(); else navigate('/connections'); return; }
        markUpgradePending('done_upsell');
        navigate('/account-subscription');
      },
      skip: (to: string) => { track('onboarding_skip', { step }); navigate(to); },
      /** Internal to useWizardEffects: the signed-out auto-link. */
      autoLink: () => linkAccount(true),
      delivered: () => setDeliveredHere(true),
    },
  };
}

export type ConnectFlowModel = ReturnType<typeof useConnectFlow>;

/**
 * What only happens where the wizard is ON SCREEN (called by ConnectFlow, so a page that loads
 * the model but shows something else — /connections once set up, the clear flow — fires no
 * onboarding events and links nothing): screen views, extension detection, the checkout
 * join, the "just connected" celebration + tab return, and the signed-out auto-link.
 */
export function useWizardEffects(m: ConnectFlowModel): { justConnected: boolean } {
  const { t } = useTranslation(namespace);
  const { ready, track, isPro, entry, hasExtension, preview, kind, substate } = m;
  const step = m.flow.screen;

  const viewed = useRef<string | null>(null);
  useEffect(() => {
    if (!ready || viewed.current === step) return;
    viewed.current = step;
    track('onboarding_view', { step });
  }, [step, track, ready]);

  // The extension answers its first ping within a second or two of load. Present by then = it was
  // already installed (count it once, separately); appearing later = installed during onboarding.
  const mountedAt = useRef(Date.now());
  const extensionCounted = useRef(false);
  useEffect(() => {
    if (!hasExtension || extensionCounted.current) return;
    extensionCounted.current = true;
    track(Date.now() - mountedAt.current < 4000 ? 'onboarding_extension_preinstalled' : 'onboarding_extension_detected');
  }, [hasExtension]); // eslint-disable-line react-hooks/exhaustive-deps

  // Back from checkout (/thank-you hands the source over in the URL) with always-on unlocked →
  // join the paywall click to the purchase. Once per visit.
  const upgradeCounted = useRef(false);
  useEffect(() => {
    if (!isPro || entry !== 'checkout' || upgradeCounted.current) return;
    upgradeCounted.current = true;
    track('onboarding_upgrade_completed', { source: new URLSearchParams(window.location.search).get('src') || 'unknown' });
  }, [isPro]); // eslint-disable-line react-hooks/exhaustive-deps

  // The moment WhatsApp connects here: celebrate, and — if the user is still in the WhatsApp tab —
  // ask the extension to bring this tab back (title flash as a fallback). Only a REAL connect: the
  // user sat on a not-connected screen for a while (4s) — a late presence update for an
  // already-connected user must never look like one.
  const [justConnected, setJustConnected] = useState(false);
  const notConnectedSince = useRef<number | null>(null);
  const flash = useRef<{ id: number; original: string; stop: () => void } | null>(null);
  const stopFlash = useCallback(() => {
    const f = flash.current;
    if (!f) return;
    window.clearInterval(f.id);
    document.title = f.original;
    document.removeEventListener('visibilitychange', f.stop);
    flash.current = null;
  }, []);
  useEffect(() => stopFlash, [stopFlash]);
  useEffect(() => {
    if (!ready) return;
    const connected = step === 'message' || step === 'done';
    if (!connected) {
      if (notConnectedSince.current === null) notConnectedSince.current = Date.now();
      return;
    }
    const since = notConnectedSince.current;
    notConnectedSince.current = null;
    if (since === null || Date.now() - since < 4000) return;
    setJustConnected(true);
    track('onboarding_connected', { kind });
    if (document.hidden && !flash.current) {
      window.postMessage({ type: 'BT_FOCUS_ME' }, window.location.origin);
      const original = document.title;
      const label = t('onboarding.tabConnected');
      let on = false;
      const id = window.setInterval(() => { on = !on; document.title = on ? label : original; }, 1000);
      const stop = () => { if (!document.hidden) stopFlash(); };
      flash.current = { id, original, stop };
      document.addEventListener('visibilitychange', stop);
    }
  }, [step, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  // Signed out = nothing to overwrite — connect it without asking, once per visit.
  const autoLinked = useRef(false);
  useEffect(() => {
    if (preview || autoLinked.current || substate !== 'signedOut' || step !== 'local') return;
    autoLinked.current = m.actions.autoLink();
  }, [preview, substate, step]); // eslint-disable-line react-hooks/exhaustive-deps

  return { justConnected };
}
